import { useQuery } from '@tanstack/react-query'

/**
 * Current weather for the island, from Open-Meteo (free, no key, no account). Location comes from
 * the browser once the person allows it; it is only used to ask for the weather and is kept in
 * this browser. No location, no weather: the island simply leaves it out.
 */

export interface Weather {
  temperatureC: number
  code: number
  isDay: boolean
  label: string
  emoji: string
}

const CACHE_KEY = 'tapwork:island:coords:v1'

function describe(code: number, isDay: boolean): { label: string; emoji: string } {
  if (code === 0) return isDay ? { label: 'Clear', emoji: '☀️' } : { label: 'Clear', emoji: '🌙' }
  if (code <= 2) return { label: 'Partly cloudy', emoji: isDay ? '⛅' : '☁️' }
  if (code === 3) return { label: 'Cloudy', emoji: '☁️' }
  if (code === 45 || code === 48) return { label: 'Fog', emoji: '🌫️' }
  if (code >= 51 && code <= 67) return { label: 'Rain', emoji: '🌧️' }
  if (code >= 71 && code <= 77) return { label: 'Snow', emoji: '🌨️' }
  if (code >= 80 && code <= 82) return { label: 'Showers', emoji: '🌦️' }
  if (code >= 95) return { label: 'Thunderstorm', emoji: '⛈️' }
  return { label: 'Weather', emoji: '🌡️' }
}

function cachedCoords(): { lat: number; lon: number } | null {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY)
    return raw ? (JSON.parse(raw) as { lat: number; lon: number }) : null
  } catch {
    return null
  }
}

function currentCoords(): Promise<{ lat: number; lon: number } | null> {
  const cached = cachedCoords()
  if (cached) return Promise.resolve(cached)
  if (!('geolocation' in navigator)) return Promise.resolve(null)
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // Rounded to ~1km: plenty for weather, less precise than we need to keep.
        const coords = { lat: Math.round(pos.coords.latitude * 100) / 100, lon: Math.round(pos.coords.longitude * 100) / 100 }
        try {
          window.localStorage.setItem(CACHE_KEY, JSON.stringify(coords))
        } catch {
          // Storage optional.
        }
        resolve(coords)
      },
      () => resolve(null),
      { maximumAge: 6 * 60 * 60 * 1000, timeout: 10_000 },
    )
  })
}

export function useWeather(enabled: boolean) {
  return useQuery({
    queryKey: ['island-weather'],
    enabled,
    staleTime: 30 * 60_000,
    refetchInterval: 30 * 60_000,
    retry: false,
    queryFn: async (): Promise<Weather | null> => {
      const coords = await currentCoords()
      if (!coords) return null
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${coords.lat}&longitude=${coords.lon}&current=temperature_2m,weather_code,is_day`
      const res = await fetch(url)
      if (!res.ok) return null
      const body = (await res.json()) as { current?: { temperature_2m?: number; weather_code?: number; is_day?: number } }
      const c = body.current
      if (!c || typeof c.temperature_2m !== 'number') return null
      const isDay = c.is_day !== 0
      return { temperatureC: Math.round(c.temperature_2m), code: c.weather_code ?? 0, isDay, ...describe(c.weather_code ?? 0, isDay) }
    },
  })
}
